import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Loader2, Target, History, Eye, X } from "lucide-react";
import { ResponsiveContainer, ComposedChart, Bar, Line, ReferenceLine, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";
import api from "@/lib/api";
import { Head, Loader, EmptyBox } from "./shared";
import BusinessPlanBuilder from "@/components/BusinessPlanBuilder";

export function BusinessPlan() {
  const [projects, setProjects] = useState(null);
  const [pid, setPid] = useState("");
  const [reminding, setReminding] = useState(false);
  const [stats, setStats] = useState(null);
  const [goalInput, setGoalInput] = useState("");
  const [savingGoal, setSavingGoal] = useState(false);
  const [log, setLog] = useState(null);
  const [showLog, setShowLog] = useState(false);
  const [digest, setDigest] = useState(null);
  const [loadingDigest, setLoadingDigest] = useState(false);
  const [schedWeekday, setSchedWeekday] = useState(0);
  const [schedHour, setSchedHour] = useState(8);
  const [savingSched, setSavingSched] = useState(false);
  const [testingDigest, setTestingDigest] = useState(false);
  const [limits, setLimits] = useState(null);
  const [savingLimits, setSavingLimits] = useState(false);

  const loadStats = () => api.get("/admin/business-plan/analytics").then((r) => {
    setStats(r.data);
    setGoalInput(String(r.data.conversion_goal ?? ""));
    if (typeof r.data.digest_weekday === "number") setSchedWeekday(r.data.digest_weekday);
    if (typeof r.data.digest_hour === "number") setSchedHour(r.data.digest_hour);
  }).catch(() => {});

  useEffect(() => {
    api.get("/admin/projects").then((r) => setProjects(r.data)).catch(() => setProjects([]));
    loadStats();
    api.get("/admin/business-plan/client-limits").then((r) => setLimits(r.data)).catch(() => {});
  }, []);

  const sendReminders = async () => {
    setReminding(true);
    try {
      const { data } = await api.post("/admin/business-plan/reminders");
      toast.success(data.sent ? `Sent ${data.sent} unlock reminder${data.sent > 1 ? "s" : ""}.` : "No clients are pending an unlock reminder right now.");
      if (data.sent) loadLog();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send reminders"); } finally { setReminding(false); }
  };

  const saveGoal = async () => {
    setSavingGoal(true);
    try {
      await api.put("/admin/business-plan/conversion-goal", { goal: Number(goalInput) || 0 });
      toast.success("Conversion goal updated.");
      loadStats();
    } catch (e) { toast.error("Could not save goal"); } finally { setSavingGoal(false); }
  };

  const loadLog = () => api.get("/admin/business-plan/reminder-log").then((r) => setLog(r.data.log || [])).catch(() => setLog([]));
  const toggleLog = () => { const next = !showLog; setShowLog(next); if (next && log === null) loadLog(); };

  const previewDigest = async () => {
    setLoadingDigest(true);
    try {
      const { data } = await api.get("/admin/business-plan/digest/preview");
      setDigest(data);
    } catch (e) { toast.error("Could not load digest preview"); } finally { setLoadingDigest(false); }
  };

  const saveSchedule = async () => {
    setSavingSched(true);
    try {
      await api.put("/admin/business-plan/digest-schedule", { weekday: Number(schedWeekday), hour: Number(schedHour) });
      toast.success("Weekly digest schedule saved.");
    } catch (e) { toast.error("Could not save schedule"); } finally { setSavingSched(false); }
  };

  const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  const hourLabel = (h) => { const ap = h < 12 ? "AM" : "PM"; const hh = h % 12 === 0 ? 12 : h % 12; return `${hh}:00 ${ap}`; };

  const testDigest = async () => {
    setTestingDigest(true);
    try {
      const { data } = await api.post("/admin/business-plan/digest/test");
      toast.success(data.delivered ? `Test digest sent to ${data.sent_to}.` : "Digest ran in Preview (no email key) — add Resend on Production to receive it.");
    } catch (e) { toast.error("Could not send test digest"); } finally { setTestingDigest(false); }
  };

  const setLimit = (k, v) => setLimits((l) => ({ ...l, [k]: v }));
  const saveLimits = async () => {
    setSavingLimits(true);
    try {
      await api.put("/admin/business-plan/client-limits", {
        grant_finder_enabled: !!limits.grant_finder_enabled,
        one_pager_enabled: !!limits.one_pager_enabled,
        pipeline_export_enabled: !!limits.pipeline_export_enabled,
        grant_finder_cap: Number(limits.grant_finder_cap) || 0,
        one_pager_cap: Number(limits.one_pager_cap) || 0,
      });
      toast.success("Client limits saved.");
    } catch (e) { toast.error("Could not save limits"); } finally { setSavingLimits(false); }
  };

  if (projects === null) return <Loader />;

  return (
    <div data-testid="admin-business-plan">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <Head title="Business Plan" desc="SBA-format business plan built from each client's Business DNA — review the client's draft, edit sections, adjust financials, approve & lock a version, and export a branded PDF." />
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={previewDigest} disabled={loadingDigest} data-testid="bp-preview-digest-btn"
            className="inline-flex items-center gap-2 text-sm border border-white/15 text-white px-4 py-2 hover:border-gold/50 transition-colors disabled:opacity-60">
            {loadingDigest ? <Loader2 className="animate-spin" size={15} /> : <Eye size={15} />} Preview digest
          </button>
          <button onClick={toggleLog} data-testid="bp-reminder-log-btn"
            className="inline-flex items-center gap-2 text-sm border border-white/15 text-white px-4 py-2 hover:border-gold/50 transition-colors">
            <History size={15} /> Reminder log
          </button>
          <button onClick={sendReminders} disabled={reminding} data-testid="bp-send-reminders"
            className="inline-flex items-center gap-2 text-sm border border-gold/40 text-gold px-4 py-2 hover:bg-gold/10 transition-colors disabled:opacity-60">
            {reminding ? <Loader2 className="animate-spin" size={15} /> : <Mail size={15} />} Send unlock reminders
          </button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="bp-analytics">
          {[
            ["Previewed", stats.previewed],
            ["Unlocked", stats.unlocked],
            ["Pending unlock", stats.pending],
            ["Conversion", `${stats.conversion_pct}%`],
          ].map(([label, val]) => (
            <div key={label} className="bg-navy-800 border border-white/10 p-3">
              <div className="text-[11px] uppercase tracking-wider text-gray-500">{label}</div>
              <div className="text-2xl font-bold text-gold mt-1">{val}</div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-navy-800 border border-white/10 p-4 mb-6 flex items-center justify-between flex-wrap gap-3" data-testid="bp-digest-schedule">
        <div>
          <div className="text-sm text-white font-semibold">Weekly digest schedule</div>
          <div className="text-xs text-gray-400 mt-0.5">Pick when your weekly summary email arrives (Central Time).</div>
        </div>
        <div className="flex items-center gap-2">
          <select value={schedWeekday} onChange={(e) => setSchedWeekday(Number(e.target.value))} data-testid="sched-weekday"
            className="bg-navy border border-white/15 text-white text-sm px-2 py-1.5 focus:border-gold focus:outline-none">
            {WEEKDAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
          </select>
          <select value={schedHour} onChange={(e) => setSchedHour(Number(e.target.value))} data-testid="sched-hour"
            className="bg-navy border border-white/15 text-white text-sm px-2 py-1.5 focus:border-gold focus:outline-none">
            {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
          </select>
          <button onClick={saveSchedule} disabled={savingSched} data-testid="sched-save"
            className="text-sm bg-gold/15 text-gold border border-gold/30 px-3 py-1.5 hover:bg-gold/25 disabled:opacity-60">
            {savingSched ? "…" : "Save"}
          </button>
          <button onClick={testDigest} disabled={testingDigest} data-testid="sched-test-send"
            className="inline-flex items-center gap-1.5 text-sm border border-white/15 text-white px-3 py-1.5 hover:border-gold/50 disabled:opacity-60">
            {testingDigest ? <Loader2 className="animate-spin" size={14} /> : <Mail size={14} />} Send test digest
          </button>
        </div>
      </div>

      {limits && (
        <div className="bg-navy-800 border border-white/10 p-4 mb-6" data-testid="bp-client-limits">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div>
              <div className="text-sm text-white font-semibold">Client access & limits</div>
              <div className="text-xs text-gray-400 mt-0.5">Control what clients can use and cap monthly usage so costs never run away. Your own Grant Finder is unlimited.</div>
            </div>
            <button onClick={saveLimits} disabled={savingLimits} data-testid="limits-save"
              className="text-sm bg-gold/15 text-gold border border-gold/30 px-3 py-1.5 hover:bg-gold/25 disabled:opacity-60">{savingLimits ? "…" : "Save limits"}</button>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            {[
              ["Grant Finder", "grant_finder_enabled", "grant_finder_cap", "AI runs / client / month"],
              ["One-Pager sends", "one_pager_enabled", "one_pager_cap", "sends / client / month"],
              ["Pipeline Export", "pipeline_export_enabled", null, null],
            ].map(([label, enKey, capKey, capNote]) => (
              <div key={enKey} className="bg-navy border border-white/10 p-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={!!limits[enKey]} onChange={(e) => setLimit(enKey, e.target.checked)} data-testid={`limit-toggle-${enKey}`} className="accent-gold w-4 h-4" />
                  <span className="text-sm text-white">{label}</span>
                </label>
                {capKey && (
                  <div className="flex items-center gap-2 mt-2">
                    <input type="number" min="0" value={limits[capKey] ?? 0} onChange={(e) => setLimit(capKey, e.target.value)} data-testid={`limit-cap-${capKey}`}
                      disabled={!limits[enKey]} className="w-16 bg-navy-800 border border-white/15 text-white text-xs px-2 py-1 focus:border-gold focus:outline-none disabled:opacity-50" />
                    <span className="text-[11px] text-gray-500">{capNote} <span className="text-gray-600">(0 = unlimited)</span></span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {showLog && (
        <div className="bg-navy-800 border border-white/10 p-4 mb-6" data-testid="bp-reminder-log">
          <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-3">Reminder History</div>
          {log === null && <div className="flex items-center gap-2 text-sm text-gray-400"><Loader2 className="animate-spin" size={14} /> Loading…</div>}
          {log !== null && log.length === 0 && <p className="text-sm text-gray-400">No reminders have been sent yet.</p>}
          {log !== null && log.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-gray-500 text-xs uppercase">
                  <th className="text-left py-1.5">Client</th><th className="text-left">1st reminder</th>
                  <th className="text-left">2nd reminder</th><th className="text-left">Status</th>
                </tr></thead>
                <tbody>
                  {log.map((r, i) => (
                    <tr key={i} className="border-t border-white/5 text-gray-200" data-testid={`reminder-log-row-${i}`}>
                      <td className="py-2">{r.org_name || r.email || "—"}</td>
                      <td className="text-gray-400">{r.first_sent ? new Date(r.first_sent).toLocaleDateString() : "—"}</td>
                      <td className="text-gray-400">{r.second_sent ? new Date(r.second_sent).toLocaleDateString() : "—"}</td>
                      <td>{r.unlocked ? <span className="text-green-400">Unlocked</span> : r.opted_out ? <span className="text-red-400">Opted out</span> : <span className="text-gold">Pending</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {stats?.trend && stats.trend.some((t) => t.previews || t.unlocks) && (
        <div className="bg-navy-800 border border-white/10 p-4 mb-6" data-testid="bp-analytics-trend">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div className="text-[11px] uppercase tracking-wider text-gray-500">Previews vs Unlocks (last 6 months)</div>
            <div className="flex items-center gap-2" data-testid="bp-goal-setter">
              <Target size={13} className="text-gold" />
              <span className="text-xs text-gray-400">Goal</span>
              <input type="number" value={goalInput} onChange={(e) => setGoalInput(e.target.value)} data-testid="bp-goal-input"
                className="w-16 bg-navy border border-white/15 text-white px-2 py-1 text-xs focus:border-gold focus:outline-none" />
              <span className="text-xs text-gray-400">%</span>
              <button onClick={saveGoal} disabled={savingGoal} data-testid="bp-goal-save"
                className="text-xs bg-gold/15 text-gold border border-gold/30 px-2.5 py-1 hover:bg-gold/25 disabled:opacity-60">
                {savingGoal ? "…" : "Set"}
              </button>
            </div>
          </div>
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer>
              <ComposedChart data={stats.trend} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="month" stroke="#94a3b8" fontSize={12} />
                <YAxis yAxisId="cnt" stroke="#94a3b8" fontSize={12} allowDecimals={false} />
                <YAxis yAxisId="pct" orientation="right" stroke="#C8A45C" fontSize={12} domain={[0, 100]} unit="%" />
                <Tooltip contentStyle={{ background: "#0B1220", border: "1px solid #334155", color: "#fff", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar yAxisId="cnt" dataKey="previews" name="Previews" fill="#64748b" radius={[3, 3, 0, 0]} />
                <Bar yAxisId="cnt" dataKey="unlocks" name="Unlocks" fill="#C8A45C" radius={[3, 3, 0, 0]} />
                <Line yAxisId="pct" type="monotone" dataKey="conversion" name="Conversion %" stroke="#22c55e" strokeWidth={2} dot={{ r: 3 }} />
                {typeof stats.conversion_goal === "number" && (
                  <ReferenceLine yAxisId="pct" y={stats.conversion_goal} stroke="#22c55e" strokeDasharray="5 4"
                    label={{ value: `Goal ${stats.conversion_goal}%`, position: "insideTopRight", fill: "#22c55e", fontSize: 10 }} />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="mb-6 max-w-md">
        <label className="block text-xs uppercase tracking-wider text-gray-500 mb-2">Choose a client project</label>
        <select value={pid} onChange={(e) => setPid(e.target.value)} data-testid="bp-project-select"
          className="w-full bg-navy-800 border border-white/15 text-white px-3 py-2.5 text-sm focus:border-gold focus:outline-none">
          <option value="">— Select a project —</option>
          {projects.map((p) => <option key={p.id} value={p.id}>{p.org_name || p.email || p.id}</option>)}
        </select>
      </div>

      {projects.length === 0 && <EmptyBox text="No client projects yet. A Business Plan is built per project." />}

      {pid && <BusinessPlanBuilder key={pid} mode="admin" projectId={pid} />}

      {digest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="digest-preview-modal" onClick={() => setDigest(null)}>
          <div className="bg-navy border border-white/15 w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <div>
                <div className="text-white font-semibold">Weekly digest preview</div>
                <div className="text-xs text-gray-400 mt-0.5">
                  This is exactly what will email to you weekly. {digest.will_send
                    ? <span className="text-amber-300">Conversion {digest.conversion}% is below your {digest.goal}% goal — a goal alert would also fire this month.</span>
                    : <span className="text-green-400">Conversion {digest.conversion}% is on track vs your {digest.goal}% goal.</span>}
                </div>
              </div>
              <button onClick={() => setDigest(null)} data-testid="digest-preview-close" className="text-gray-400 hover:text-white"><X size={18} /></button>
            </div>
            <iframe title="digest-preview" srcDoc={digest.html} data-testid="digest-preview-frame"
              className="w-full" style={{ height: 520, border: "none", background: "#0b1220" }} />
          </div>
        </div>
      )}
    </div>
  );
}
